/**
 * Monthly progress: Present=1, Double Session=2, Absent=0.
 * A resolved Excused (make-up "Done on …") counts as 1 — the lesson was
 * actually delivered, so it credits both the teacher's target and finance.
 * Unresolved Excused = 0.
 * Target = scheduled days per week × 4.
 */

export function isResolvedMakeup(record) {
    return (
        record &&
        record.status === "Excused" &&
        typeof record.remark === "string" &&
        record.remark.startsWith("Done")
    );
}

export function statusValue(statusOrRecord, maybeRemark) {
    // Accept either (record) — preferred, so remark is available — or
    // (status, remark) for callers that pass them separately.
    let status;
    let remark;
    if (statusOrRecord && typeof statusOrRecord === "object") {
        status = statusOrRecord.status;
        remark = statusOrRecord.remark;
    } else {
        status = statusOrRecord;
        remark = maybeRemark;
    }
    if (status === "Present") return 1;
    if (status === "Double Session") return 2;
    if (status === "Excused" && typeof remark === "string" && remark.startsWith("Done")) {
        return 1;
    }
    return 0;
}

export function monthKey(dateStr) {
    // dateStr = YYYY-MM-DD
    return dateStr.slice(0, 7);
}

export function currentMonthKey(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}

export function computeMonthlyProgress(student, attendance, _month, multiplier = 4) {
    // "Month" for Tempo is a session-count cycle — 4 lessons per scheduled
    // weekday (or `multiplier` per weekday). We do NOT bucket by calendar
    // month because students start lessons on different dates and pay per
    // package of N sessions.
    //
    // lifetimeUnits = sum of statusValue across all attendance for the student.
    // cycle           = which package the student is currently on (1-based).
    // units           = position within the current cycle (1..target once any
    //                   lesson is logged; 0 only when nothing is logged yet).
    // complete        = current cycle is filled (units === target).
    const target = (student.days?.length || 0) * multiplier;
    const lifetimeUnits = attendance
        .filter((r) => r.studentId === student.id)
        .reduce((sum, r) => sum + statusValue(r), 0);

    let cycle = 1;
    let units = 0;
    if (lifetimeUnits > 0 && target > 0) {
        cycle = Math.floor((lifetimeUnits - 1) / target) + 1;
        units = ((lifetimeUnits - 1) % target) + 1;
    }

    const pct = target === 0 ? 0 : Math.min(100, Math.round((units / target) * 100));
    return {
        units,
        target,
        pct,
        complete: target > 0 && units === target && lifetimeUnits > 0,
        cycle,
        lifetimeUnits,
    };
}

export function todayStr() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function formatDatePretty(dateStr) {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

export function formatDateShort(dateStr) {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Relative time from an ISO timestamp (or Date) — e.g. "just now", "5m ago",
 * "2h ago", "Yesterday", or a short date for older entries.
 */
export function formatRelativeTime(value, now = new Date()) {
    if (!value) return "";
    const then = value instanceof Date ? value : new Date(value);
    if (isNaN(then.getTime())) return "";
    const diffMs = now.getTime() - then.getTime();
    const sec = Math.round(diffMs / 1000);
    if (sec < 45) return "just now";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const dayDiff = Math.round((startOfToday - startOfThen) / 86400000);
    if (dayDiff === 1) return "Yesterday";
    if (dayDiff < 7) return `${dayDiff}d ago`;
    return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
